import { Braze } from '@/core/types'
import { AuthUser } from '@bees-web/core/types'
import { useEffect, useState } from 'react'

const enableLogging = process.env.STAGE !== 'prod'

const loadBraze = () => import('@braze/web-sdk')

const changeBrazeUser = (braze, userInfos: AuthUser & { userId: string }) => {
  const { userId, email, phone } = userInfos

  braze.automaticallyShowInAppMessages()
  braze.changeUser(userId)
  braze.openSession()

  const user = braze.getUser()
  if (!user) return

  user.setEmail(email)
  user.setPhoneNumber(phone)
}

const setComplementaryData = (braze, userInfos: AuthUser) => {
  const { firstName, lastName, ...otherInfos } = userInfos
  const user = braze.getUser()

  if (!user) return

  user.setFirstName(firstName)
  user.setLastName(lastName)

  Object.entries(otherInfos).forEach(([k, v]) => {
    user.setCustomUserAttribute(k, v as string)
  })
}

interface UseBraze {
  userId?: string
  country: string
  pathname: string
  trackingConsented?: boolean
  oneTrustInitialized?: boolean
  userInfos?: AuthUser
  configuration: Braze
}

const useBraze = ({
  userId,
  country,
  pathname,
  trackingConsented = false,
  oneTrustInitialized = false,
  userInfos,
  configuration,
}: UseBraze) => {
  const [brazeStarted, setBrazeStarted] = useState<boolean>(false)
  const [brazeInitialized, setBrazeInitialized] = useState<boolean>(false)

  const optOutInApp = configuration?.optOutInApp ?? true

  useEffect(() => {
    if (
      pathname === '/' ||
      brazeStarted ||
      !oneTrustInitialized ||
      (!trackingConsented && !optOutInApp)
    )
      return

    if (process.env.STAGE !== 'prod') console.log('[useBraze] Initializing SDK')
    setBrazeStarted(true)
    if (!configuration?.key) {
      console.warn(`[useBraze] No Braze configuration found for country: ${country}`)
      return
    }
    const { key, host: baseUrl } = configuration
    loadBraze().then((braze) => {
      window.braze = braze
      window.braze.initialize(key, {
        baseUrl,
        enableLogging,
        allowUserSuppliedJavascript: true,
      })
      changeBrazeUser(window.braze, { userId, ...userInfos })
      setBrazeInitialized(true)
    })
  }, [
    brazeStarted,
    oneTrustInitialized,
    userInfos,
    trackingConsented,
    country,
    configuration,
    optOutInApp,
    pathname,
    userId,
  ])

  useEffect(() => {
    if (typeof window !== 'undefined' && window.braze && userId) {
      try {
        if (window.braze.getUser().getUserId() !== userId) {
          changeBrazeUser(window.braze, { userId, ...userInfos })
        }
      } catch (error) {
        console.warn('[Braze] Failed to change user:', error)
      }
    }
  }, [userId, userInfos])

  useEffect(() => {
    if (
      oneTrustInitialized &&
      brazeInitialized &&
      typeof window !== 'undefined' &&
      window.braze &&
      trackingConsented
    ) {
      try {
        setComplementaryData(window.braze, userInfos)
      } catch (error) {
        console.warn('[Braze] Failed to set complementary data:', error)
      }
    }
  }, [oneTrustInitialized, brazeInitialized, trackingConsented, userInfos])

  return brazeInitialized
}

export default useBraze
